import Hidden from 'raydiant-elements/layout/Hidden';
import Spacer from 'raydiant-elements/layout/Spacer';
import { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as R from '../../clients/mira/types/Resource';
import { selectUserProfile } from '../../selectors/user';
import { SortFolderOptions, canCreateFolderItem } from '../../utilities';
import { selectSearchQuery, selectSortOptions } from './selectors';
import * as actions from './actions';
import * as libraryActions from '../../pages/LibraryPage/actions';
import { useDebounce } from 'use-debounce';
import {
  ArrowDownArrowUp,
  Filter,
  Search,
  XMark,
} from '@raydiant/raydial/components/Icons';
import Button from '@raydiant/raydial/components/Button';
import cn from 'classnames';
import Popover from '@raydiant/raydial/components/Popover';
import Dialog from '@raydiant/raydial/components/Dialog';

interface PlaylistSelectorActionBarProps {
  selectedProfile?: R.ResourceProfile;
  sortDisabled?: boolean;
  onNewPlaylist?: () => void;
  sortOnly?: boolean;
}

const Grid2Plus = () => {
  return (
    <svg
      width="17"
      height="16"
      viewBox="0 0 17 16"
      fill="inherit"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.47461 2.05078L2.28711 2.05078L2.28711 5.23828L5.47461 5.23828V2.05078ZM2.28711 0.457031L5.47461 0.457031C6.33789 0.457031 7.06836 1.1875 7.06836 2.05078V5.23828C7.06836 6.13477 6.33789 6.83203 5.47461 6.83203L2.28711 6.83203C1.39062 6.83203 0.693359 6.13477 0.693359 5.23828L0.693359 2.05078C0.693359 1.1875 1.39062 0.457031 2.28711 0.457031ZM5.47461 10.5508L2.28711 10.5508L2.28711 13.7383L5.47461 13.7383L5.47461 10.5508ZM2.28711 8.95703L5.47461 8.95703C6.33789 8.95703 7.06836 9.6875 7.06836 10.5508L7.06836 13.7383C7.06836 14.6348 6.33789 15.332 5.47461 15.332H2.28711C1.39062 15.332 0.693359 14.6348 0.693359 13.7383L0.693359 10.5508C0.693359 9.6875 1.39062 8.95703 2.28711 8.95703ZM10.7871 2.05078L10.7871 5.23828L13.9746 5.23828L13.9746 2.05078L10.7871 2.05078ZM9.19336 2.05078C9.19336 1.1875 9.89062 0.457031 10.7871 0.457031L13.9746 0.457031C14.8379 0.457031 15.5684 1.1875 15.5684 2.05078L15.5684 5.23828C15.5684 6.13477 14.8379 6.83203 13.9746 6.83203H10.7871C9.89062 6.83203 9.19336 6.13477 9.19336 5.23828L9.19336 2.05078ZM12.3809 8.42578C12.8125 8.42578 13.1777 8.79102 13.1777 9.22266V11.3477H15.3027C15.7344 11.3477 16.0996 11.7129 16.0996 12.1445C16.0996 12.6094 15.7344 12.9414 15.3027 12.9414H13.1777L13.1777 15.0664C13.1777 15.5313 12.8125 15.8633 12.3809 15.8633C11.916 15.8633 11.584 15.5313 11.584 15.0664L11.584 12.9414H9.45898C8.99414 12.9414 8.66211 12.6094 8.66211 12.1445C8.66211 11.7129 8.99414 11.3477 9.45898 11.3477H11.584V9.22266C11.584 8.79102 11.916 8.42578 12.3809 8.42578Z"
        fill="inherit"
      />
    </svg>
  );
};

const SEARCH_DEBOUNCE_TIMEOUT = 3000; //in ms

const PlaylistSelectorActionBarV2 = ({
  selectedProfile,
  sortDisabled,
  onNewPlaylist,
  sortOnly = false,
}: PlaylistSelectorActionBarProps) => {
  const dispatch = useDispatch();

  // Selectors

  const sortOptions = useSelector(selectSortOptions);
  const searchQuery = useSelector(selectSearchQuery);
  const currentUser = useSelector(selectUserProfile);

  // State

  const [isSortMode, setIsSortMode] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [debouncedSearchTerm] = useDebounce(
    searchQuery,
    SEARCH_DEBOUNCE_TIMEOUT,
  );

  const triggerRef = useRef(null);

  // Callbacks

  const toggleSort = useCallback(
    (property: SortFolderOptions['property']) => {
      let direction: SortFolderOptions['direction'] =
        property === 'updatedAt' ? 'desc' : 'asc';

      if (property === sortOptions.property) {
        direction = sortOptions.direction === 'asc' ? 'desc' : 'asc';
      }

      dispatch(actions.setSortOptions({ property, direction }));

      setIsSortMode(false);
    },
    [dispatch, setIsSortMode, sortOptions],
  );

  const onSearch = useCallback(
    (search: string) => {
      dispatch(actions.setSearchQuery(search));

      setIsSortMode(false);
    },
    [dispatch],
  );

  useEffect(() => {
    let params: libraryActions.LoadLibraryPageParams = {
      profileId: selectedProfile?.id,
    };

    if (debouncedSearchTerm.length) params.search = debouncedSearchTerm;

    dispatch(libraryActions.loadLibraryPage(params));
  }, [dispatch, selectedProfile?.id, debouncedSearchTerm]);

  // Side-effects
  // Reset search query when exiting search mode.
  useEffect(() => {
    if (!isSearchMode) dispatch(actions.setSearchQuery(''));
  }, [dispatch, isSearchMode]);

  // Render

  let sortLabel = 'Sort';
  if (sortOptions.property === 'name') {
    sortLabel = `${sortLabel}: Name`;
  } else if (sortOptions.property === 'updatedAt') {
    sortLabel = `${sortLabel}: Date`;
  }

  const isSortByName = sortOptions.property === 'name';
  const isSortByDate = sortOptions.property === 'updatedAt';
  const isReverseSort = sortOptions.direction === 'desc';

  const sortActionOptions = (
    <div className="flex flex-col text-base font-regular text-gray-700 gap-1">
      <Button
        variant="unstyled"
        fullWidth
        className={cn(
          'flex items-center px-4 py-2.5 hover:bg-gray-100',
          isSortByName && 'bg-gray-200 hover:bg-gray-200',
        )}
        onClick={() => toggleSort('name')}
      >
        {isSortByName && isReverseSort ? (
          <ArrowDownArrowUp />
        ) : (
          <ArrowDownArrowUp transform="scale(-1,1)" />
        )}
        <span className="pl-3">Name</span>
      </Button>
      <Button
        variant="unstyled"
        fullWidth
        className={cn(
          'flex items-center px-4 py-2.5 hover:bg-gray-100',
          isSortByDate && 'bg-gray-200 hover:bg-gray-200',
        )}
        onClick={() => toggleSort('updatedAt')}
      >
        {isSortByDate && isReverseSort ? (
          <ArrowDownArrowUp />
        ) : (
          <ArrowDownArrowUp transform="scale(-1,1)" />
        )}
        <span className="pl-3">Date</span>
      </Button>
    </div>
  );

  const sortAction = (
    <>
      <Button
        variant="unstyled"
        className={cn(
          'text-lg font-medium text-gray-400 hover:text-blue-600 outline-none md:w-[114px]',
          isSortMode && 'text-blue-600',
        )}
        onPress={() => setIsSortMode(true)}
      >
        <div className="flex items-center" ref={triggerRef}>
          <Filter />
          <span className="hidden pl-2.5 md:block">{sortLabel}</span>
        </div>
      </Button>
      <Popover
        triggerRef={triggerRef}
        isOpen={isSortMode}
        onOpenChange={setIsSortMode}
        className="w-[116px]"
        placement="bottom start"
      >
        <Dialog className="px-0 py-2.5" aria-label="sort options">
          <div className="overflow-hidden">{sortActionOptions}</div>
        </Dialog>
      </Popover>
    </>
  );

  const newPlaylistDisabled =
    currentUser &&
    selectedProfile &&
    !canCreateFolderItem(currentUser, selectedProfile);

  const newPlaylist = (
    <Button
      variant="unstyled"
      className="ml-4 fill-gray-400 hover:fill-blue-600"
      onPress={onNewPlaylist}
      isDisabled={newPlaylistDisabled}
    >
      <Grid2Plus />
    </Button>
  );

  const searchAction = (
    <>
      {isSearchMode ? (
        <div className="flex text-lg font-medium text-gray-400">
          <input
            autoFocus
            className="outline-none border-b-2 max-w-[120px] mr-1"
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
          />
          <Button
            variant="unstyled"
            className="text-gray-400 hover:text-blue-600 outline-none"
            onPress={() => setIsSearchMode(false)}
          >
            <XMark size={12} />
          </Button>
        </div>
      ) : (
        <Button
          variant="unstyled"
          className="flex items-center text-lg font-medium text-gray-400 enabled:hover:text-blue-600 outline-none"
          onPress={() => setIsSearchMode(true)}
        >
          <Search size={17} />
        </Button>
      )}
    </>
  );

  return (
    <div className="flex bg-white px-4 rounded-3xl h-10 items-center border-gray-200 border-2">
      <Hidden smDown>
        {sortAction}
        {!sortOnly && onNewPlaylist && newPlaylist}
        <Spacer />
        {searchAction}
      </Hidden>

      <Hidden mdUp>
        {sortAction}
        {!isSearchMode && !sortOnly && onNewPlaylist && newPlaylist}
        {searchAction}
      </Hidden>
    </div>
  );
};

export default PlaylistSelectorActionBarV2;
